import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useLinkPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Caption from '@zenbusiness/common-components/Caption';
import Text from '@zenbusiness/common-components/Text';
import Title from '@zenbusiness/common-components/Title';
import PropTypes from 'prop-types';
import { useParams, Link as RouterLink } from 'react-router-dom';

import HelpIcon from '@mui/icons-material/Help';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';

import { readLastUpdatedDate } from '../../utils/date';

const StyledText = styled(Text)(({ theme, disabled }) => ({
  width: '100%',
  maxWidth: 300,
  ...(disabled && { color: theme.palette.focus30.main }),

  [theme.breakpoints.up('md')]: {
    maxWidth: 500
  }
}));

const StyledTitleGrid = styled(Grid)(({ theme }) => ({
  order: 1,
  marginRight: theme.spacing(1.5)
}));

const StyledStatusGrid = styled(Grid)(({ theme }) => ({
  order: 3,
  flexBasis: '100%',

  [theme.breakpoints.up('md')]: {
    order: 2,
    flexBasis: 'auto'
  }
}));

const StyledActionGrid = styled(Grid)(({ theme }) => ({
  marginLeft: 'auto',
  marginRight: theme.spacing(-2),
  order: 3,
  [theme.breakpoints.down('sm')]: {
    order: 2
  }
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  height: 1,
  background: theme.palette.focus30.main
}));

const StyledEmptyGrid = styled(Grid)(() => ({
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  backgroundSize: 'contain',
  width: '100%',
  height: '150px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center'
}));

const StyledTitle = styled(Title)(({ theme, disabled }) => ({
  textTransform: 'uppercase',
  width: '100%',
  maxWidth: '50%',
  ...(disabled && { color: theme.palette.focus30.main })
}));

const StyledInnerCard = styled(Card)(() => ({
  overflow: 'visible'
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(2, 0)
}));

const StyledHelpIcon = styled(HelpIcon)(({ theme }) => ({
  ...theme.typography.title_smallRegular,
  fontSize: '1rem',
  marginLeft: theme.spacing(0.5),
  marginBottom: theme.spacing(-0.5)
}));

const StyledButton = styled(Button)(({ theme }) => ({
  paddingTop: theme.spacing(0.5),
  textTransform: 'none',
  color: theme.palette.peace40.main
}));

const StyledCardActions = styled(CardActions)(({ theme }) => ({
  marginRight: theme.spacing(-2),
  paddingRight: 0,
  justifyContent: 'right'
}));

const messages = {
  locked: 'Record is non-editable. Please contact Customer Support to edit this record.'
};

const DnsCards = ({
  domain
}) => {
  const { businessEntityUuid } = useBusinessEntity();
  const { domainUuid } = useParams();
  const lastUpdated = readLastUpdatedDate(domain.domainDns);
  const {
    domainDns: domainDnsAll,
    useZenbusinessNameservers
  } = domain;
  const domainDns = domainDnsAll && domainDnsAll.filter(({ deleted }) => (!deleted));

  // TODO: Change useLinkPressedCallback by a button_pressed - Clicks
  const handleAddClick = useLinkPressedCallback({
    elementName: 'add_dns',
    pageCategory: 'dashboard',
    pageSection: 'growth',
    screenName: 'domain_settings_page'
  });

  // TODO: Change useLinkPressedCallback by a button_pressed - Clicks
  const handleEditClick = useLinkPressedCallback({
    elementName: 'edit_dns',
    pageCategory: 'dashboard',
    pageSection: 'growth',
    screenName: 'domain_settings_page'
  });

  let dnsCards;
  if (!useZenbusinessNameservers) {
    dnsCards = (
      <StyledEmptyGrid item>
        <Text size="sm" align="center">Your DNS records are managed through your nameserver provider.</Text>
        <Caption size="lg" color="focus50" align="center">
          To manage DNS records here,&nbsp;
          <Link component={RouterLink} to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}/nameservers`}>
            switch to using ZenBusiness nameservers
          </Link>
          .
        </Caption>
      </StyledEmptyGrid>
    );
  } else if (!domainDns || domainDns.length <= 0) {
    dnsCards = (
      <StyledEmptyGrid item>
        <Text size="lg">You haven&apos;t setup your DNS yet.</Text>
      </StyledEmptyGrid>
    );
  } else {
    dnsCards = domainDns
      .sort((d1, d2) => {
        const subdomain1 = d1.dnsConfiguration.subdomain ? d1.dnsConfiguration.subdomain.toLowerCase() : '';
        const subdomain2 = d2.dnsConfiguration.subdomain ? d2.dnsConfiguration.subdomain.toLowerCase() : '';
        const type1 = d1.type ? d1.type.toLowerCase() : '';
        const type2 = d2.type ? d2.type.toLowerCase() : '';

        if (subdomain1 === subdomain2) {
          if (type1 < type2) {
            return -1;
          }
          if (type1 > type2) {
            return 1;
          }
          return 0;
        }
        if (subdomain1 < subdomain2) {
          return -1;
        }
        return 1;
      })
      .map((dns) => {
        if (!dns) {
          return null;
        }
        return (
          <Grid
            key={dns.domainDnsUuid}
            item
          >
            <StyledInnerCard elevation={0}>
              <Grid container item xs={12} direction="row">
                <Grid item xs={10}>
                  <StyledCardContent>
                    {dns.type && (
                    <>
                      <Caption size="lg" color="focus50">
                        Type
                        {!dns.editable && (
                        <Tooltip title={messages.locked}>
                          <StyledHelpIcon size="small" color="primary" />
                        </Tooltip>
                        )}
                      </Caption>
                      <StyledTitle disabled={!dns.editable} level={3} variant="sm" gutterBottom>{dns.type}</StyledTitle>
                    </>
                    )}
                    <Caption size="lg" color="focus50">Record Data</Caption>
                    {dns.dnsConfiguration?.hostname
                      && <StyledText disabled={!dns.editable} size="sm" noWrap>{dns.dnsConfiguration.hostname}</StyledText>}
                    {dns.dnsConfiguration?.ipAddress
                      && <StyledText disabled={!dns.editable} size="sm" noWrap>{dns.dnsConfiguration.ipAddress}</StyledText>}
                    {dns.dnsConfiguration?.text
                      && <StyledText disabled={!dns.editable} size="sm" noWrap>{dns.dnsConfiguration.text}</StyledText>}
                    {dns.dnsConfiguration?.priority
                      && <StyledText disabled={!dns.editable} size="sm" noWrap>{dns.dnsConfiguration.priority}</StyledText>}
                    {dns.dnsConfiguration?.subdomain
                      && <StyledText disabled={!dns.editable} size="sm" noWrap>{dns.dnsConfiguration.subdomain}</StyledText>}
                    {dns.dnsConfiguration?.port
                      && <StyledText disabled={!dns.editable} size="sm" noWrap>{dns.dnsConfiguration.port}</StyledText>}
                    {dns.dnsConfiguration?.weight
                      && <StyledText disabled={!dns.editable} size="sm" noWrap>{dns.dnsConfiguration.weight}</StyledText>}
                    {dns.dnsConfiguration?.destinationUrl
                      && <StyledText disabled={!dns.editable} size="sm" noWrap>{dns.dnsConfiguration.destinationUrl}</StyledText>}
                  </StyledCardContent>
                </Grid>
                <Grid xs={2} item>
                  <StyledCardActions>
                    {dns.editable && (
                    <StyledButton size="small" color="primary" variant="text" component={RouterLink} onClick={handleEditClick} to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}/dns/${dns.domainDnsUuid}`}>Edit</StyledButton>
                    )}
                  </StyledCardActions>
                </Grid>
              </Grid>
            </StyledInnerCard>
          </Grid>
        );
      });
  }

  return (
    <Grid id="dns" item xs={12}>
      <Grid
        container
        item
        xs={12}
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-end"
      >
        <StyledTitleGrid item>
          <Text size="md">DNS Records</Text>
        </StyledTitleGrid>
        {lastUpdated && useZenbusinessNameservers && (
        <StyledStatusGrid item>
          <Caption size="lg" color="focus50">
            {`Last updated: ${lastUpdated}`}
          </Caption>
        </StyledStatusGrid>
        )}
        <StyledActionGrid item>
          <StyledButton variant="text" color="primary" size="small" onClick={handleAddClick} disabled={!useZenbusinessNameservers} component={RouterLink} to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}/dns`}>Add</StyledButton>
        </StyledActionGrid>
      </Grid>
      <StyledDivider />
      <Grid
        container
        item
        xs={12}
        spacing={0}
        direction="column"
        justifyContent="flex-start"
      >
        {dnsCards}
      </Grid>
    </Grid>
  );
};

/* eslint-disable react/forbid-prop-types */
DnsCards.propTypes = {
  domain: PropTypes.object.isRequired
};
/* eslint-enable react/forbid-prop-types */

export default DnsCards;
